import { yupResolver } from '@hookform/resolvers/yup';
import Modal from 'components/desktop/shared/modal';
import ToastStack from 'components/desktop/shared/toast-stack';
import {
  IconLoading,
  IconLocationFill,
  IconPanHorizontal,
  IconPanVertical,
  IconStairs,
  IconVectorSquareClose,
} from 'components/icons';
import { useAuthenticationContext } from 'components/providers/authentication-provider';
import FormTextArea from 'components/shared/form-text-area';
import FormTextInput from 'components/shared/form-text-input';
import {
  ContactForeclosureForm,
  contactForeclosureFormDefaultValues,
  contactForeclosureFormSchema,
} from 'forms';
import {
  SEND_FORECLOSURE_REQUEST,
  SendForeclosureRequestData,
  SendForeclosureRequestVariables,
} from 'graphql/main/mutations';
import { FileWithFullUrls } from 'graphql/main/queries';
import { useFlexMutation, useToast } from 'hooks';
import { filter, includes, isEqual, isNil, map, some } from 'lodash';
import { DetailPageContext } from 'pages/[ket-qua-tim-kiem]/[chi-tiet-tin-rao]';
import { Fragment, useContext, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

interface Props {
  onClose?: () => void;
}

const ContactForeclosureModal = ({ onClose }: Props) => {
  const { currentUser } = useAuthenticationContext();
  const { realEstate } = useContext(DetailPageContext).pageData ?? {};
  const {
    id,
    postTitle,
    mediaUrls,
    isForSell,
    postType,
    isAuthorizedRe,
    isForeclosure,
    fullAddress,
    area,
    width,
    length,
    floorCount,
  } = realEstate ?? {};
  const form = useForm<ContactForeclosureForm>({
    mode: 'onChange',
    resolver: yupResolver(contactForeclosureFormSchema),
    defaultValues: contactForeclosureFormDefaultValues,
  });
  const image = filter(mediaUrls, (media) => isEqual('image', media.type))?.[0];
  const stack = [
    {
      icon: <IconVectorSquareClose className='h-[24px] w-[24px] text-primary' />,
      content: area?.toLocaleString(),
      suffix: 'm\u00B2',
    },
    {
      icon: <IconPanHorizontal className='h-[24px] w-[24px] text-primary' />,
      content: width?.toLocaleString(),
      suffix: 'm',
    },
    {
      icon: <IconPanVertical className='h-[24px] w-[24px] text-primary' />,
      content: length?.toLocaleString(),
      suffix: 'm',
    },
    {
      icon: <IconStairs className='h-[24px] w-[24px] text-primary' />,
      content: floorCount?.toLocaleString(),
      suffix: 'tầng',
    },
  ];
  const [sendForeclosureRequest, { loading: loadingSendForeclosureRequest }] = useFlexMutation<
    SendForeclosureRequestData,
    SendForeclosureRequestVariables
  >('main', SEND_FORECLOSURE_REQUEST);
  const { toasts, addToast, removeToast } = useToast();

  const submit = async (data: ContactForeclosureForm) => {
    const { fullName, phone, email, note } = data;
    if (id) {
      const { errors } = await sendForeclosureRequest({
        variables: {
          fullName,
          phone,
          note,
          email,
          realEstateId: id,
        },
        onCompleted: (data) => {
          data.sendForeclosureRequest?.success
            ? addToast(
                'success',
                data.sendForeclosureRequest?.msg ?? 'Gửi liên hệ mua tài sản phát mãi thành công',
              )
            : addToast(
                'error',
                data.sendForeclosureRequest?.msg ?? 'Gửi liên hệ mua tài sản phát mãi thất bại',
              );
        },
        onError: (error) => addToast('error', error.message),
      });
      if (errors) {
        addToast('error', errors[0].message ?? 'Gửi liên hệ mua tài sản phát mãi thất bại');
      }
    }
  };
  const cancelModal = () => {
    form.reset();
    onClose?.();
  };

  useEffect(() => {
    currentUser
      ? form.reset({ fullName: currentUser.fullName, phone: currentUser.phones?.[0] })
      : null;
  }, [currentUser]);

  return (
    <Fragment>
      <Modal
        closable
        title='Liên hệ mua tài sản phát mãi'
        className='fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden'
        onClose={cancelModal}
      >
        <div className='mx-[-24px] mb-[-24px] mt-[-8px] flex'>
          <div className='flex w-[453px] flex-col space-y-[24px]'>
            <div className='relative h-[316px]'>
              <img
                src={image ? (image as FileWithFullUrls).originalUrl : '/images/error-image.svg'}
                alt={postTitle}
                loading='lazy'
                className='h-full w-full object-cover'
              />
              <div className='absolute left-[8px] top-[8px] z-[1] flex space-x-[8px]'>
                {!isNil(isForSell) && (
                  <span
                    className={`rounded-[4px] px-[8px] py-[4px] text-[11px] font-[500] uppercase leading-[14px] text-paper ${
                      isForSell ? 'bg-accent-sale' : 'bg-accent-rent'
                    }`}
                  >
                    {isForSell ? 'bán' : 'thuê'}
                  </span>
                )}
                {(isAuthorizedRe || isForeclosure) && (
                  <span className='rounded-[4px] bg-accent-sale px-[8px] py-[4px] text-[11px] font-[500] uppercase leading-[14px] text-paper'>
                    {isAuthorizedRe ? 'ký gửi' : 'phát mãi'}
                  </span>
                )}
                {includes([1, 2], postType) && (
                  <span className='rounded-[4px] bg-accent-sale px-[8px] py-[4px] text-[11px] font-[500] uppercase leading-[14px] text-paper'>
                    {isEqual(postType, 1) ? 'vip đặc biệt' : 'vip 1'}
                  </span>
                )}
              </div>
            </div>
            <div className='flex flex-col space-y-[24px] px-[24px] pb-[24px]'>
              {postTitle && (
                <span className='line-clamp-2 text-[16px] font-[700] leading-[20px]'>
                  {postTitle}
                </span>
              )}
              {fullAddress && (
                <div className='flex items-center space-x-[6px]'>
                  <IconLocationFill className='mt-[1px] h-[24px] min-w-[24px] text-text2' />
                  <span className='line-clamp-2'>{fullAddress}</span>
                </div>
              )}
              {some(stack, (item) => item.content) && (
                <div className='m-[-8px] flex max-h-[80px] flex-wrap items-center overflow-hidden'>
                  {map(
                    stack,
                    (stackItem, stackItemIndex) =>
                      stackItem.content && (
                        <div
                          key={stackItemIndex}
                          className='flex items-center space-x-[8px] whitespace-nowrap p-[8px]'
                        >
                          {stackItem.icon}
                          <span>
                            {stackItem.content} {stackItem.suffix}
                          </span>
                        </div>
                      ),
                  )}
                </div>
              )}
            </div>
          </div>
          <FormProvider {...form}>
            <form
              className='flex w-[407px] flex-col justify-between space-y-[28px] bg-[#fafafa] p-[24px]'
              onSubmit={form.handleSubmit(submit)}
            >
              <div className='flex flex-col space-y-[28px]'>
                <FormTextInput
                  maxLength={118}
                  label='Họ và tên'
                  required
                  name='fullName'
                  autoComplete='off'
                  error={form.formState.errors.fullName}
                />
                <FormTextInput
                  label='Số điện thoại'
                  required
                  name='phone'
                  maxLength={10}
                  autoComplete='off'
                  error={form.formState.errors.phone}
                />
                <FormTextInput
                  label='Email'
                  maxLength={68}
                  name='email'
                  autoComplete='off'
                  error={form.formState.errors.email}
                />
                <FormTextArea
                  label='Ghi chú'
                  name='note'
                  autoComplete='off'
                  rows={4}
                  maxLength={888}
                  error={form.formState.errors.note}
                />
              </div>
              <button
                disabled={loadingSendForeclosureRequest}
                className='flex h-[40px] w-full items-center justify-center space-x-[6px] rounded-[8px] bg-primary text-paper transition duration-[200ms] ease-in-out hover:bg-primary-light disabled:opacity-50 disabled:hover:bg-primary'
                onClick={form.handleSubmit(submit)}
              >
                {loadingSendForeclosureRequest && <IconLoading className='animate-spin' />}
                <span className='font-[600]'>Gửi liên hệ</span>
              </button>
            </form>
          </FormProvider>
        </div>
      </Modal>
      <ToastStack toasts={toasts} removeToast={removeToast} />
    </Fragment>
  );
};

export default ContactForeclosureModal;
